import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation, Outlet } from 'react-router-dom';
import { PageLayout } from '../../components/layout';
import { FailedState } from '../../components/ui/LoadingState';
import { Button } from '../../components/ui/Button2';
import ProjectHeader from './components/ProjectHeader/ProjectHeader';
import { WorkspaceChromeProvider } from './WorkspaceChromeContext';
import { getSession } from '../../api/sessions';
import { getWorkflowStatus } from '../../api/workflow';
import { loadingBarStart } from '../../services/loadingBar';
import styles from './ProjectWorkspace.module.css';

// Project-specific navigation items for the sub-header. Order
// follows the user's product flow (input → analysis → residual →
// compliance → reports → meta), so left-to-right roughly mirrors
// the lifecycle of a TARA. Tab `id` values are stable — they're
// the `?tab=` URL keys and the switch cases in renderTabContent().
const PROJECT_NAV_ITEMS = [
  { id: 'overview',           label: 'Overview',           icon: 'results',      description: 'View analysis results' },
  { id: 'architecture',       label: 'Architecture',       icon: 'diagram',      description: 'System architecture diagrams' },
  // Epic 12 — v2 workflow is the canonical TARA tab. Legacy
  // `AthenaWorkflowOrchestrator` was deleted in Story 13B.
  { id: 'tara-workflow',      label: 'TARA',               icon: 'workflow',     description: 'Run TARA analysis workflow' },
  { id: 'attack-trees',       label: 'Attack Trees',       icon: 'attackTree',   description: 'Attack tree visualizations' },
  // The v2 redesign is now the canonical Residual TARA tab (sunset of
  // the legacy Step10ResidualTARA page completed 2026-05-27 — `id` and
  // label stay `residual-tara` / "Residual TARA").
  { id: 'residual-tara',      label: 'Residual TARA',      icon: 'residualTara', description: 'Residual risk assessment after control implementation' },
  { id: 'unr155-checklist',   label: 'UN R155 Checklist',  icon: 'checklist',    description: 'UN R155 compliance checklist' },
  { id: 'compliance-mapping', label: 'Compliance Mapping', icon: 'compliance',   description: 'ISO/UN R155 compliance mapping' },
  { id: 'traceability',       label: 'Traceability',       icon: 'traceability', description: 'Threat traceability matrix across all TARA artifacts' },
  // PR #185 added the Traceability Map alongside the matrix view.
  // The accompanying nav entry was dropped during the develop→epic/12
  // merge (dacb5e9db) since PROJECT_NAV_ITEMS was being reordered on
  // both sides; the lazy import + case branch survived but became
  // unreachable. Restored here next to its sibling matrix view.
  { id: 'traceability-map',   label: 'Traceability Map',   icon: 'diagram',      description: 'Visual flow diagram of threats through the full TARA chain' },
  { id: 'reports',            label: 'Reports',            icon: 'report',       description: 'Download analysis reports' },
  { id: 'versions',           label: 'Versions',           icon: 'history',      description: 'Workflow version history' },
  { id: 'integrations',       label: 'Integrations',       icon: 'link',         description: 'External system integrations' },
  { id: 'configuration',      label: 'Configuration',      icon: 'gear',         description: 'TARA calculation settings' },
];

const DEFAULT_TAB = 'overview';
const VALID_TAB_IDS = new Set(PROJECT_NAV_ITEMS.map((item) => item.id));

const ProjectWorkspace = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0); // Slide navigation state
  const [workflowStatus, setWorkflowStatus] = useState(null); // Workflow progress data
  const [agentResults, setAgentResults] = useState({}); // Cache of agent results

  // FU-001 (2026-05-27): active tab is now derived from the URL path
  // (`/project/:projectId/<tabId>`) instead of a `?tab=` query param.
  // The default tab keeps the URL clean (`/project/:projectId` with no
  // segment renders the index route, which is the Overview tab).
  // Bookmarks pointing at the old v2-tab id (`tara-workflow-v2`) are
  // handled by an explicit redirect route in projects/routes.jsx.
  const pathSegments = location.pathname.split('/').filter(Boolean);
  // ['project', '<projectId>', '<tabId>?', ...]
  const tabSegment = pathSegments[2];
  const activeProjectTab =
    tabSegment && VALID_TAB_IDS.has(tabSegment) ? tabSegment : DEFAULT_TAB;

  const setActiveTab = useCallback(
    (tabId) => {
      if (!VALID_TAB_IDS.has(tabId)) return;
      // Keep the URL clean for the default tab — `/project/<id>` with
      // no segment renders the index Overview route.
      const target =
        tabId === DEFAULT_TAB ? `/project/${projectId}` : `/project/${projectId}/${tabId}`;
      navigate(target, { replace: true });
    },
    [navigate, projectId],
  );

  // Listen for cross-component tab navigation requests (e.g. from Step9 → Residual TARA)
  useEffect(() => {
    const handler = (e) => {
      const tab = e.detail?.tab;
      if (tab) {
        setActiveTab(tab);
        window.scrollTo({ top: 0, behavior: 'instant' });
      }
    };
    window.addEventListener('navigate-project-tab', handler);
    return () => window.removeEventListener('navigate-project-tab', handler);
  }, [setActiveTab]);

  // Agent name mapping for steps 1-9
  const STEP_TO_AGENT_MAP = {
    1: 'analysis_agent',
    2: 'asset_identification_agent',
    3: 'damage_scenario_agent',
    4: 'threat_analysis_agent',
    5: 'attack_path_feasibility_agent',
    6: 'risk_assessment_agent',
    7: 'cybersecurity_goals_agent',
    8: 'cybersecurity_concept_agent',
    9: 'report_generation_agent'
  };

  // Reverse mapping: agent name to step number
  const AGENT_TO_STEP_MAP = Object.entries(STEP_TO_AGENT_MAP).reduce((acc, [step, agent]) => {
    acc[agent] = parseInt(step);
    return acc;
  }, {});

  // Get the last completed step number based on agents_completed
  const getLastCompletedStep = (agentsCompleted) => {
    if (!agentsCompleted || agentsCompleted.length === 0) return 0;

    // Find the highest step number from completed agents
    const completedSteps = agentsCompleted
      .map(agent => AGENT_TO_STEP_MAP[agent])
      .filter(step => step !== undefined);

    return completedSteps.length > 0 ? Math.max(...completedSteps) : 0;
  };

  // Get step-specific icon
  const getStepIcon = (stepNumber) => {
    const icons = {
      1: ( // Item Definition
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
          <polyline points="14 2 14 8 20 8"/>
          <line x1="16" y1="13" x2="8" y2="13"/>
          <line x1="16" y1="17" x2="8" y2="17"/>
          <polyline points="10 9 9 9 8 9"/>
        </svg>
      ),
      2: ( // Asset Identification
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="2" y="7" width="20" height="14" rx="2" ry="2"/>
          <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/>
        </svg>
      ),
      3: ( // Damage Scenarios
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
          <line x1="12" y1="9" x2="12" y2="13"/>
          <line x1="12" y1="17" x2="12.01" y2="17"/>
        </svg>
      ),
      4: ( // Threat Identification
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
          <path d="M12 8v4"/>
          <path d="M12 16h.01"/>
        </svg>
      ),
      5: ( // Attack Path Feasibility
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
        </svg>
      ),
      6: ( // Risk Assessment
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <line x1="18" y1="20" x2="18" y2="10"/>
          <line x1="12" y1="20" x2="12" y2="4"/>
          <line x1="6" y1="20" x2="6" y2="14"/>
        </svg>
      ),
      7: ( // Cybersecurity Goals
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10"/>
          <path d="M12 6v6l4 2"/>
        </svg>
      ),
      8: ( // Cybersecurity Concept
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
          <polyline points="7.5 4.21 12 6.81 16.5 4.21"/>
          <polyline points="7.5 19.79 7.5 14.6 3 12"/>
          <polyline points="21 12 16.5 14.6 16.5 19.79"/>
          <polyline points="3.27 6.96 12 12.01 20.73 6.96"/>
          <line x1="12" y1="22.08" x2="12" y2="12"/>
        </svg>
      ),
      9: ( // Final Report
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
          <polyline points="14 2 14 8 20 8"/>
          <line x1="16" y1="13" x2="8" y2="13"/>
          <line x1="16" y1="17" x2="8" y2="17"/>
          <line x1="10" y1="9" x2="8" y2="9"/>
        </svg>
      )
    };
    return icons[stepNumber] || null;
  };

  // Fetch workflow status. Returned to the caller so it can derive
  // initial UI state (e.g. project status fallbacks).
  //
  // Epic 12 — the per-agent `getAgentResult` preflight loop that used
  // to live here populated the legacy sidebar's `agentResults` cache.
  // With the legacy sidebar retired, WorkflowV2 fetches its own step
  // results on demand, so the N+1 preflight has been removed. Status
  // (a single call) is still pulled because `currentSlideIndex` and a
  // few status-banner branches still consume `agents_completed`.
  const fetchWorkflowData = async (threadId) => {
    if (!threadId) return null;

    try {
      const statusData = await getWorkflowStatus(threadId);
      setWorkflowStatus(statusData);
      return statusData;
    } catch (err) {
      console.error('Error fetching workflow data:', err);
    }

    return null;
  };

  // Fetch project details
  useEffect(() => {
    // Guards a projectId switch mid-flight: state writes from a stale
    // fetch are dropped.
    let cancelled = false;

    // Determine initial slide based on workflow progress
    // For IN_PROGRESS/PAUSED/COMPLETED: No welcome slide, so Step N = Slide (N-1)
    // For NEW: Slide 0 = Welcome screen
    const deriveSlide = (data, workflowStatusData) => {
      if (data.session_status === 'COMPLETED') {
        // For completed analyses, show Item Definition (step 1 = slide 0)
        setCurrentSlideIndex(0);
      } else if (data.session_status === 'IN_PROGRESS' || data.session_status === 'PAUSED') {
        // Use agents_completed to find the last completed step
        let currentStep = 0;

        // First try to get from agents_completed field
        if (workflowStatusData?.agents_completed && workflowStatusData.agents_completed.length > 0) {
          currentStep = getLastCompletedStep(workflowStatusData.agents_completed);
        }

        // If no agents completed yet, try other sources
        if (currentStep === 0) {
          currentStep = data.current_step ||
                       data.workflow_progress?.current_agent_index ||
                       data.progress?.current_step || 0;
        }

        if (currentStep > 0) {
          // Show the last completed step (Step N = Slide N-1)
          setCurrentSlideIndex(currentStep - 1);
        } else {
          // If no step completed but workflow exists, start at step 1 (slide 0)
          if (data.thread_id) {
            setCurrentSlideIndex(0);
          }
        }
      }
      // else: Keep slide 0 (welcome) for brand new analyses (NEW status, no thread_id)
    };

    const fetchProject = async () => {
      // Token on the top progress bar for the project fetch.
      const doneLoadingBar = loadingBarStart();
      try {
        setLoading(true);
        // Reset stale data from previous project immediately
        setWorkflowStatus(null);
        setAgentResults({});
        setCurrentSlideIndex(0);
        let data;
        try {
          data = await getSession(projectId);
        } catch (err) {
          if (err?.status === 404) {
            throw new Error('Project not found');
          }
          throw new Error('Failed to fetch project details');
        }
        if (cancelled) return;

        // Shell-first: the workspace unblocks on getSession alone —
        // nothing in the initial render consumes workflowStatus, so
        // the status fetch continues in the background below.
        setProject(data);
        setError(null);
        setLoading(false);

        if (data.thread_id) {
          // fetchWorkflowData catches internally and returns null on error.
          fetchWorkflowData(data.thread_id).then((workflowStatusData) => {
            if (cancelled) return;
            deriveSlide(data, workflowStatusData);
          });
        } else {
          deriveSlide(data, null);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message);
          setLoading(false);
        }
        console.error('Error fetching project:', err);
      } finally {
        doneLoadingBar();
      }
    };

    if (projectId) {
      fetchProject();
    }
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  // Poll workflow status while analysis is in progress to keep sidebar indicators updated
  useEffect(() => {
    if (!project?.thread_id || project?.session_status === 'COMPLETED' || project?.session_status === 'NEW') {
      return;
    }

    const interval = setInterval(() => {
      fetchWorkflowData(project.thread_id);
    }, 5000);

    return () => clearInterval(interval);
  }, [project?.thread_id, project?.session_status]);

  // Handle back navigation
  const handleBackToProjects = () => {
    navigate('/projects');
  };

  // Handle project sub-navigation (tabs rendered inline, no page navigation)
  const handleProjectNavigation = (itemId) => {
    setActiveTab(itemId);
    window.scrollTo({ top: 0, behavior: 'instant' });
  };

  // FU-001: tab content now renders through `<Outlet />` (see JSX
  // below). Each child route in projects/routes.jsx wraps a tab
  // component that reads its props from `useOutletContext()` —
  // see features/projects/components/WorkspaceTabs.jsx.

  // Shell-first content slot — the chrome (PageLayout + ProjectHeader)
  // renders immediately, and the active tab mounts DURING the project
  // fetch: every tab owns its loading UI, rendering its own skeleton
  // off the `projectLoading` outlet-context flag (see WorkspaceTabs.jsx
  // for the contract). The skeleton IS the loading state, together with
  // the top progress bar — no spinner phase, no anti-flicker delay.

  /* Every project tab runs in the same bounded-layout model — the
     outer .mainContent never scrolls. ProjectHeader sits pinned at
     the top of the flex column; .tabContentContainer below it is
     the single scroll boundary. Tabs that own their own internal
     scroll (Architecture / Attack Trees table body, TARA Workflow
     StepShell) fill the container exactly and use their own scroll;
     tabs that flow naturally (Overview cards, Reports, Versions,
     etc.) scroll inside the container. Result: project header +
     tab strip stay fixed regardless of which tab is open. */

  // Edge-to-edge tabs strip the tabContentContainer's 2rem horizontal
  // gutter so the embedded chrome reaches the viewport edges. Used by
  // the v2 workflow (StepShell owns its own gutter system), the
  // Architecture / Attack Trees list pages (their borderless DataTable
  // shell already fills its container, so the outer gutter would just
  // duplicate framing), the Configuration tab (its sidebar + content
  // pane already form the visual container), and the Residual TARA
  // tab (same rail-+-content layout as Configuration).
  const isEdgeToEdgeTab =
    activeProjectTab === 'tara-workflow' ||
    activeProjectTab === 'architecture' ||
    activeProjectTab === 'attack-trees' ||
    activeProjectTab === 'configuration' ||
    activeProjectTab === 'residual-tara' ||
    activeProjectTab === 'unr155-checklist' ||
    activeProjectTab === 'traceability';

  return (
    <PageLayout
      activeNavItem="projects"
      contentClassName={styles.fullWidthContent}
      disableScroll
    >
      <WorkspaceChromeProvider>
        <div className={styles.workspaceContainer}>
        <ProjectHeader
          project={project}
          navItems={PROJECT_NAV_ITEMS}
          activeNavItem={activeProjectTab}
          onNavigation={handleProjectNavigation}
        />

        {/* Tab content — each tab is a child route under
            /project/:projectId; <Outlet /> renders the active one and
            passes workspace state through context. Workspace state
            (project, projectId, callbacks) is exposed via the outlet
            context for tab wrappers in WorkspaceTabs.jsx. */}
        <div
          className={`${styles.tabContentContainer} ${isEdgeToEdgeTab ? styles.tabContentContainerEdge : ''}`}
        >
          <div className={styles.tabContentInner}>
            {!loading && error ? (
              <FailedState
                variant="page"
                title="Error Loading Project"
                description={error}
                action={
                  <Button variant="outline" onClick={handleBackToProjects}>
                    Back to Projects
                  </Button>
                }
              />
            ) : !loading && !project ? (
              <FailedState
                variant="page"
                title="No project data available"
                action={
                  <Button variant="outline" onClick={handleBackToProjects}>
                    Back to Projects
                  </Button>
                }
              />
            ) : (
              // Tabs mount DURING the project fetch with project=null +
              // projectLoading=true and render their own skeleton inside
              // their real chrome; when the fetch lands the same instance
              // receives the data — skeleton→data with no remount and no
              // spinner phase.
              <Outlet
                context={{
                  project,
                  projectId,
                  projectLoading: loading,
                  workflowStatus,
                  agentResults,
                  setAgentResults,
                  fetchWorkflowData,
                }}
              />
            )}
          </div>
        </div>
        </div>
      </WorkspaceChromeProvider>
    </PageLayout>
  );
};

export default ProjectWorkspace;